import Barcode from "react-barcode";

import dayjs from "dayjs";
import { AlertCircle, Box, Calendar, Hash, Package, X } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { usePositionInventory } from "~/hooks/wms";
import { cn } from "~/lib/utils";
import type { WmsInventory } from "~/types/wms";
import { generateLocationQrCode } from "~/utils/barcodeParser";

interface PositionInventoryCardProps {
  positionId: string;
  onClose: () => void;
}

function getDaysUntilExpiry(
  expirationDate: string | null | undefined
): number | null {
  if (!expirationDate) return null;
  const expiry = dayjs(expirationDate);
  const today = dayjs();
  return expiry.diff(today, "day");
}

function getExpiryBadgeVariant(
  daysUntilExpiry: number | null
): "destructive" | "warning" | "secondary" | "default" {
  if (daysUntilExpiry === null) return "secondary";
  if (daysUntilExpiry < 30) return "destructive";
  if (daysUntilExpiry < 90) return "warning";
  return "default";
}

function getExpiryColor(daysUntilExpiry: number | null): string {
  if (daysUntilExpiry === null) return "text-muted-foreground";
  if (daysUntilExpiry < 30) return "text-red-600";
  if (daysUntilExpiry < 90) return "text-yellow-600";
  return "text-green-600";
}

interface InventoryItemProps {
  inventory: WmsInventory;
}

function InventoryItem({ inventory }: InventoryItemProps) {
  const daysUntilExpiry = getDaysUntilExpiry(inventory.expiration_date);
  // NEUTRALIZED: reserved_boxes ignored - available = box_count
  const availableBoxes = inventory.box_count || 0;

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <div>
            <h4 className="font-medium">
              {inventory.product?.short_name || "Producto desconocido"}
            </h4>
            {inventory.lot_number && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Hash className="h-3 w-3" />
                <span>Lote: {inventory.lot_number}</span>
              </div>
            )}
          </div>
        </div>
        {daysUntilExpiry !== null && (
          <Badge variant={getExpiryBadgeVariant(daysUntilExpiry)}>
            {daysUntilExpiry < 0
              ? "Vencido"
              : daysUntilExpiry === 0
                ? "Vence hoy"
                : `${daysUntilExpiry} días`}
          </Badge>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        {/* Total boxes */}
        <div className="rounded-md bg-slate-50 p-2 text-center">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-lg font-semibold">
            {inventory.box_count || 0}
          </div>
          <div className="text-xs text-muted-foreground">cajas</div>
        </div>

        {/* NEUTRALIZED: Reserved boxes section hidden */}

        {/* Available boxes */}
        <div className="rounded-md bg-green-50 p-2 text-center">
          <div className="text-xs text-green-600">Disponibles</div>
          <div className="text-lg font-semibold text-green-700">
            {availableBoxes}
          </div>
          <div className="text-xs text-green-600">cajas</div>
        </div>
      </div>

      {/* Expiration date */}
      {inventory.expiration_date && (
        <div className="mt-3 flex items-center gap-2">
          <Calendar
            className={cn("h-4 w-4", getExpiryColor(daysUntilExpiry))}
          />
          <span className="text-sm">
            Vence:{" "}
            <span
              className={cn("font-medium", getExpiryColor(daysUntilExpiry))}
            >
              {dayjs(inventory.expiration_date).format("DD/MM/YYYY")}
            </span>
          </span>
        </div>
      )}

      {/* Pallet info */}
      {inventory.pallet && (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Box className="h-3 w-3" />
          <span>
            Pallet: {inventory.pallet.barcode || inventory.pallet.code}
          </span>
        </div>
      )}
    </div>
  );
}

export function PositionInventoryCard({
  positionId,
  onClose,
}: PositionInventoryCardProps) {
  const { data, isLoading, error } = usePositionInventory(positionId);

  const position = data?.position;
  const inventory = data?.inventory || [];

  if (isLoading) {
    return (
      <Card className="mt-4 border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-48" />
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-4 border-red-200 bg-red-50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle className="h-4 w-4" />
              Error al cargar inventario
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600">
            No se pudo cargar el inventario de esta posición.
          </p>
        </CardContent>
      </Card>
    );
  }

  const hasInventory = inventory.length > 0;

  return (
    <Card className="mt-4 border-primary/20 bg-primary/5">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            {position?.full_code ? position.full_code.replace(/^[^-]+-/, "") : `Posición ${positionId.slice(0, 8)}...`}
            {position?.level && (
              <Badge variant="outline" className="ml-2">
                Nivel {position.level}
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Location barcode */}
        {position?.full_code && (
          <div className="mb-4 rounded-lg border bg-white p-3">
            <div className="mb-2 text-center text-xs text-muted-foreground">
              Código de ubicación
            </div>
            <div className="flex flex-col items-center">
              <Barcode
                value={generateLocationQrCode(position.full_code)}
                format="CODE128"
                width={1.2}
                height={40}
                displayValue={false}
                margin={0}
              />
              <p className="mt-1 font-mono text-xs">
                {position.full_code.replace(/^[^-]+-/, "")}
              </p>
            </div>
          </div>
        )}

        {!hasInventory ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Package className="mb-2 h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Esta posición está vacía
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {inventory.map((inv) => (
              <InventoryItem key={inv.id} inventory={inv} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
